/**
 * Vistory / Meta Whale brand palette (navy, royal blue, gold, cyan, pale sky).
 */
import { createTheme } from "@mui/material/styles";

export const brandColors = {
  navy: "#0B1C48",
  navyDark: "#001A4D",
  navyMid: "#002366",
  royal: "#1E40AF",
  sky: "#0072CE",
  cyan: "#00BFFF",
  cyanLight: "#60A5FA",
  gold: "#D4AF37",
  goldDark: "#C5A028",
  white: "#FFFFFF",
  background: "#F0F8FF",
  backgroundAlt: "#E6F0FA",
  textOnDark: "#F8FAFC",
  textMuted: "rgba(11, 28, 72, 0.72)",
};

/** MUI-style scale used across components as `primaryBlue` */
export const brandPalette = {
  50: brandColors.background,
  100: brandColors.backgroundAlt,
  200: "#C5DDF5",
  300: brandColors.cyanLight,
  400: brandColors.sky,
  500: brandColors.royal,
  600: brandColors.navyMid,
  700: brandColors.navyDark,
  800: brandColors.navy,
  900: "#050F24",
};

export const primaryBlue = brandPalette;

export const brandGlassStyles = {
  background: "rgba(255, 255, 255, 0.42)",
  backdropFilter: "blur(20px)",
  border: `1px solid rgba(212, 175, 55, 0.28)`,
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(11, 28, 72, 0.14)",
};

export const brandCardGlassStyles = {
  background: "rgba(255, 255, 255, 0.55)",
  backdropFilter: "blur(15px)",
  border: `1px solid rgba(212, 175, 55, 0.22)`,
  borderRadius: "20px",
  boxShadow: "0 8px 32px rgba(11, 28, 72, 0.12)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 16px 48px rgba(11, 28, 72, 0.2)",
    background: "rgba(255, 255, 255, 0.68)",
    borderColor: "rgba(212, 175, 55, 0.45)",
  },
};

export const brandNavBarStyles = {
  bgcolor: brandColors.navy,
  backdropFilter: "blur(12px)",
  borderBottom: `2px solid ${brandColors.gold}`,
  borderRadius: 0,
  color: brandColors.textOnDark,
};

export function getBrandPageBackground(palette = brandPalette) {
  return `linear-gradient(135deg, 
    ${palette[50]} 0%, 
    ${palette[100]} 25%, 
    ${palette[200]} 50%, 
    ${palette[100]} 75%, 
    ${palette[50]} 100%)`;
}

/** Category / chip gradients — brand colours only */
export const brandGradientPairs = [
  [brandColors.navyDark, brandColors.gold],
  [brandColors.royal, brandColors.cyan],
  [brandColors.navy, brandColors.sky],
  [brandColors.sky, brandColors.cyanLight],
  [brandColors.goldDark, brandColors.navyMid],
  [brandColors.navyMid, brandColors.cyan],
  [brandColors.royal, brandColors.gold],
  [brandColors.cyan, brandColors.navyDark],
];

export function createBrandMuiTheme() {
  return createTheme({
    palette: {
      mode: "light",
      primary: {
        main: brandColors.navyMid,
        light: brandColors.sky,
        dark: brandColors.navyDark,
        contrastText: brandColors.white,
      },
      secondary: {
        main: brandColors.gold,
        dark: brandColors.goldDark,
        contrastText: brandColors.navyDark,
      },
      background: {
        default: brandColors.background,
        paper: brandColors.white,
      },
      text: {
        primary: brandColors.navy,
        secondary: brandColors.textMuted,
      },
      info: {
        main: brandColors.sky,
      },
      success: {
        main: brandColors.cyan,
        dark: brandColors.sky,
      },
      warning: {
        main: brandColors.gold,
        dark: brandColors.goldDark,
      },
      error: {
        main: brandColors.navyDark,
        light: brandColors.royal,
      },
    },
    typography: {
      fontFamily:
        '"Roboto", "Segoe UI", system-ui, -apple-system, sans-serif',
      h4: { fontWeight: 700, color: brandColors.navyDark },
      h5: { fontWeight: 700, color: brandColors.navyDark },
      h6: { fontWeight: 600, color: brandColors.navyMid },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            background: `linear-gradient(45deg, ${brandColors.navyMid}, ${brandColors.royal})`,
            "&:hover": {
              background: `linear-gradient(45deg, ${brandColors.navyDark}, ${brandColors.navyMid})`,
            },
          },
          containedSecondary: {
            background: `linear-gradient(45deg, ${brandColors.goldDark}, ${brandColors.gold})`,
            color: brandColors.navyDark,
            fontWeight: 700,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: brandColors.navy,
            borderBottom: `2px solid ${brandColors.gold}`,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: brandColors.background,
          },
        },
      },
    },
  });
}
